/**
 * How a link to a theme token is written down — and why it depends on where it
 * is being stored.
 *
 * The picker is one widget serving two very different destinations:
 *
 *   a style prop in CODE   →  getThemeValue("colors.primary")
 *   a Live Layer FIELD     →  colors.primary
 *
 * The first is a call because it lands in a source file, where that text has to
 * BE the expression that reads the token at render time. The second is a
 * published JSON value: nothing evaluates it, so a call there is not a call at
 * all — it is a 31-character string that travels to the device and gets handed
 * to a style.
 *
 * Storing the call form as data was the original mistake, and it fails in a
 * quiet way. An app's lookup keys on the first path segment, and in the wrapped
 * form the first `.` is no longer the separator — the segment is
 * `getThemeValue("colors`, which matches no group in the theme, so the lookup
 * correctly decides this is a plain value and passes it straight through into a
 * style. The element renders colourless, with a working resolver the whole time.
 *
 * So the format is a property of the destination, not of the picker, and this
 * module is the only place that knows it. Reads accept BOTH forms — payloads
 * published before this split still hold call text, and `normalizeThemeRefs`
 * migrates them.
 */

/** Where the link is going, which is what decides how it is written. */
export type ThemeRefFormat =
  /** Into source, where it must be an expression. */
  | 'call'
  /** Into Live Layer data, where it must be a bare token path. */
  | 'path';

/** The token groups a link may point at. */
const GROUPS = ['colors', 'typography'] as const;

/**
 * `getThemeValue("colors.primary")`.
 *
 * Unanchored on purpose: a style value can legitimately carry the call inside a
 * larger expression, and the read path has always been lenient here.
 */
const CALL_RE = /getThemeValue\(\s*['"]([A-Za-z0-9_.]+)['"]\s*\)/;

/**
 * `colors.primary`, anchored and group-qualified.
 *
 * The qualification is what makes this safe to run over arbitrary values: a
 * colour is a hex, an `rgba(…)`, or a CSS name, and none of those begin
 * `colors.` or `typography.`. Matching a bare dotted word would swallow real
 * data.
 */
const PATH_RE = new RegExp(`^(?:${GROUPS.join('|')})\\.[A-Za-z0-9_]+(?:\\.[A-Za-z0-9_]+)*$`);

/** The token path a value links to, in either form. Null when it is not a link. */
export function themeRefPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (PATH_RE.test(trimmed)) return trimmed;
  const m = CALL_RE.exec(trimmed);
  return m ? m[1] : null;
}

/** True for the legacy call form, wherever it is still stored. */
export function isThemeRefCall(value: unknown): boolean {
  return typeof value === 'string' && CALL_RE.test(value.trim());
}

/** Write a link the way its destination needs to receive it. */
export function writeThemeRef(path: string, format: ThemeRefFormat): string {
  return format === 'call' ? `getThemeValue("${path}")` : path;
}

/**
 * The `valueType` that goes with a format.
 *
 * A call is an expression the host may need to place in scope; a path is an
 * ordinary string datum, and calling it an expression would invite a caller to
 * treat published JSON as code.
 */
export function themeRefValueType(format: ThemeRefFormat): 'expression' | 'string' {
  return format === 'call' ? 'expression' : 'string';
}

/**
 * Rewrite every legacy call form in a published payload to a bare path.
 *
 * Runs over the whole value tree on the way out, so data published before the
 * split migrates the next time its panel publishes, rather than needing a
 * separate sweep. Returns the input by identity when nothing changed, so an
 * unaffected publish neither allocates nor looks dirty.
 */
export function normalizeThemeRefs<T>(value: T): T {
  if (typeof value === 'string') {
    const m = CALL_RE.exec(value.trim());
    // Only a value that is ENTIRELY the call becomes a path. A call embedded in
    // a larger expression is code that happens to be stored as text, and
    // rewriting it would corrupt it.
    if (!m || value.trim() !== m[0]) return value;
    return m[1] as unknown as T;
  }

  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => {
      const nv = normalizeThemeRefs(v);
      if (nv !== v) changed = true;
      return nv;
    });
    return (changed ? out : value) as unknown as T;
  }

  if (value && typeof value === 'object' && !(value instanceof Date)) {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const nv = normalizeThemeRefs(v);
      if (nv !== v) changed = true;
      out[k] = nv;
    }
    return (changed ? out : value) as unknown as T;
  }

  return value;
}
